import React, { useEffect, useMemo, useRef } from "react";
import {
  ColDef,
  GridApi,
  GridOptions,
  IServerSideGetRowsParams,
  PaginationChangedEvent,
  SelectionChangedEvent,
  SortChangedEvent,
  ValueGetterParams,
} from "@ag-grid-community/core";
import { Box } from "@mui/material";
import { difference, differenceBy, pullAll } from "lodash";

import { DEFAULT_PAGE_LIMIT } from "fond/constants";
import { Attachment } from "fond/types";

import { AgGrid, fileSizeValueFormatter, LinkCellRenderer, MimeTypeCellRenderer, stringComparator } from "../AgGrid";

import { Sort } from "./types";

export type ListViewProps = {
  files: Attachment[];
  columnDefs?: ColDef[];
  onSelectionChange(files: Attachment[]): void;
  handleZoomOpen(index: number): void;
  selected: Attachment[];
  pagination?: boolean;
  getRows: (params: IServerSideGetRowsParams) => Promise<void>;
  sort: Sort | null;
  setSort(value: Sort | null): void;
};

const ListView: React.FC<ListViewProps> = ({
  files,
  columnDefs,
  onSelectionChange,
  handleZoomOpen,
  pagination,
  selected,
  getRows,
  sort,
  setSort,
}) => {
  const selectedFileIds = selected.map(({ ID }) => ID);
  const gridApi = useRef<GridApi | null>(null);
  const previousFiles = useRef<Attachment[] | null>(null);

  // if files is updated, check if there is a difference between it and the grid's data.
  useEffect(() => {
    const api = gridApi.current;
    if (!api) {
      return;
    }

    // don't do anything if this is the first time the files are being set.
    if (!previousFiles.current) {
      previousFiles.current = files;
      return;
    }

    // separate the difference into new, updated, and deleted files to make it easy to apply the changes to the grid.
    const added = differenceBy(files, previousFiles.current, "ID");
    const updated = pullAll(difference(files, previousFiles.current), added);
    const removed = differenceBy(previousFiles.current, files, "ID");

    api.applyServerSideTransactionAsync({ add: added, update: updated, remove: removed });

    previousFiles.current = files;
  }, [files]);

  const defaultColumns: ColDef[] = useMemo(
    () => [
      {
        field: "MimeType",
        headerName: "Type",
        minWidth: 50,
        width: 50,
        suppressHeaderMenuButton: true,
        cellRenderer: MimeTypeCellRenderer,
        cellClass: "vertically-align",
        resizable: false,
      },
      {
        field: "Name",
        headerName: "Name",
        minWidth: 100,
        flex: 1,
        suppressHeaderMenuButton: true,
        valueGetter: (params: ValueGetterParams) => `${params.data.Name}.${params.data.Extension}`,
        cellRenderer: LinkCellRenderer,
        cellRendererParams: {
          onClick: (targetFile: Attachment) => () => {
            const index = files.findIndex((file) => file.ID === targetFile.ID);
            handleZoomOpen(index);
          },
        },
        comparator: stringComparator,
        initialSort: sort === null ? "asc" : null,
      },
      {
        field: "Size",
        headerName: "Size",
        valueFormatter: fileSizeValueFormatter,
        width: 80,
        type: "rightAligned",
        suppressHeaderMenuButton: true,
      },
    ],
    [files, handleZoomOpen]
  );

  /**
   * Set the 'selectedItems' state variable from the IDs of the currently selected row items.
   */
  const handleOnSelection = (event: SelectionChangedEvent) => {
    const newSelection: Attachment[] = [];
    event.api.forEachNode((node) => {
      if (node.isSelected()) {
        newSelection.push(node.data);
      }
    });
    onSelectionChange(newSelection);
  };

  const handleOnSortChanged = (event: SortChangedEvent) => {
    if (!event.columns) return;
    const changedColumn = event.columns[0];
    const columnId = changedColumn.getColId() as keyof Attachment;
    const columnSort = changedColumn.getSort();
    if (columnSort == null) {
      setSort(null);
    } else {
      setSort({ colId: columnId, sort: columnSort });
    }
  };

  const handlePaginationChanged = (event: PaginationChangedEvent) => {
    // Handle selection of currently checked files
    event.api.forEachNode((node) => {
      if (node.data && selectedFileIds.includes(node.data.ID)) {
        node.setSelected(true);
      }
    });
  };

  const handleGridReady = (event: { api: GridApi }) => {
    gridApi.current = event.api;

    if (sort) {
      event.api.applyColumnState({ state: [sort] });
    }
  };

  const gridOptions: GridOptions = useMemo(
    () => ({
      animateRows: true,
      rowGroupPanelShow: "never",
      rowSelection: {
        mode: "multiRow",
        checkboxes: true,
      },
      rowModelType: "serverSide",
      cacheBlockSize: DEFAULT_PAGE_LIMIT,
      serverSideDatasource: { getRows },
      sideBar: false,
      paginationAutoPageSize: pagination,
      pagination,
      getRowId: ({ data }) => data.ID,
      suppressMultiSort: true,
      suppressMovableColumns: true,
      selectionColumnDef: { width: 32 },
    }),
    [getRows, pagination]
  );

  return (
    <Box data-testid="files-viewer-list" height="100%">
      <AgGrid
        columnDefs={columnDefs ?? defaultColumns}
        size="compact"
        gridOptions={gridOptions}
        onSelectionChanged={handleOnSelection}
        onSortChanged={handleOnSortChanged}
        variant="borderless"
        onPaginationChanged={handlePaginationChanged}
        onGridReady={handleGridReady}
      />
    </Box>
  );
};

export default ListView;
